import React, { useState, useEffect, useRef } from 'react';

function WhyChooseUsNew() {
  const [projectsCount, setProjectsCount] = useState(0);
  const [citiesCount, setCitiesCount] = useState(0);
  const [hasAnimated, setHasAnimated] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const currentSection = sectionRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            
            // Animate projects counter
            let projectStart = 0;
            const projectEnd = 1000;
            const projectDuration = 2000;
            const projectIncrement = projectEnd / (projectDuration / 16);
            
            const projectTimer = setInterval(() => {
              projectStart += projectIncrement;
              if (projectStart >= projectEnd) {
                setProjectsCount(projectEnd);
                clearInterval(projectTimer);
              } else {
                setProjectsCount(Math.floor(projectStart));
              }
            }, 16);

            // Animate cities counter
            let cityStart = 0;
            const cityEnd = 50;
            const cityDuration = 2000;
            const cityIncrement = cityEnd / (cityDuration / 16);
            
            const cityTimer = setInterval(() => {
              cityStart += cityIncrement;
              if (cityStart >= cityEnd) {
                setCitiesCount(cityEnd);
                clearInterval(cityTimer);
              } else {
                setCitiesCount(Math.floor(cityStart));
              }
            }, 16);
          }
        });
      },
      { threshold: 0.3 }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, [hasAnimated]);

  return (
    <section id="why-choose-us" className="bg-[#1a1a1a] px-5 py-20 sm:px-6 md:px-12 md:py-24 lg:px-20 lg:py-32" ref={sectionRef}>
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16">
          <div className="lg:col-span-5 reveal">
            <h2 className="bank-gothic text-xs tracking-[0.5em] text-neutral-500 mb-6 uppercase">Why Choose Us</h2>
            <h3 className="bank-gothic text-3xl sm:text-4xl mb-8 md:mb-10 leading-tight">
              Architectural <span className="accent-text">Integrity</span> <br />at Scale.
            </h3>
            <p className="text-neutral-400 text-sm leading-relaxed mb-8 font-light">
              We don't just design buildings; we manage legacies. Our approach integrates rigorous certification with a nationwide operational footprint, ensuring every brick laid meets our high standards.
            </p>
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="mt-1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="accent-text" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="bank-gothic text-[10px] text-white">PAN India Operations</h4>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="mt-1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="accent-text" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="bank-gothic text-[10px] text-white">In-House Design Teams</h4>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="mt-1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="accent-text" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="bank-gothic text-[10px] text-white">ISO Certified Company</h4>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="mt-1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="accent-text" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="bank-gothic text-[10px] text-white">On-Time Completion Legacy</h4>
                </div>
              </div>
              <div className="flex items-start space-x-4">
                <div className="mt-1">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" className="accent-text" strokeWidth="2">
                    <polyline points="20 6 9 17 4 12"></polyline>
                  </svg>
                </div>
                <div>
                  <h4 className="bank-gothic text-[10px] text-white">End-to-End Service Guarantee</h4>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5 border border-white/5 reveal" style={{ transitionDelay: '200ms' }}>
            <div className="bg-[#1a1a1a] p-8 sm:p-10 md:p-12 flex flex-col justify-center">
              <span className="accent-text bank-gothic text-4xl sm:text-5xl mb-4">{projectsCount}+</span>
              <h4 className="bank-gothic text-[10px] text-white tracking-widest">Projects</h4>
            </div>
            <div className="bg-[#1a1a1a] p-8 sm:p-10 md:p-12 flex flex-col justify-center md:border-l border-white/5">
              <span className="accent-text bank-gothic text-4xl sm:text-5xl mb-4">{citiesCount}+</span>
              <h4 className="bank-gothic text-[10px] text-white tracking-widest">Cities</h4>
            </div>
            <div className="bg-[#1a1a1a] p-8 sm:p-10 md:p-12 flex flex-col justify-center border-t border-white/5">
              <span className="accent-text bank-gothic text-4xl sm:text-5xl mb-4">2000</span>
              <h4 className="bank-gothic text-[10px] text-white tracking-widest">Since</h4>
            </div>
            <div className="bg-[#1a1a1a] p-8 sm:p-10 md:p-12 flex flex-col justify-center border-t border-white/5 md:border-l">
              <span className="accent-text bank-gothic text-4xl sm:text-5xl mb-4">ISO</span>
              <h4 className="bank-gothic text-[10px] text-white tracking-widest">Certified</h4>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default WhyChooseUsNew;
